import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  List,
  ListItemText,
  ListItemButton,
  useTheme,
  TextField,
  InputAdornment,
  ButtonGroup,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Event as EventIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isToday,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
} from 'date-fns';
import { useSnackbar } from 'notistack';
import { PageHeader, LoadingSpinner, EmptyState } from '../../components/common';
import { EventFormDialog } from '../../components/dialogs';
import { useEvents } from '../../hooks';
import { colors } from '../../theme';
import type { Event, ParticipantInput } from '../../types';

const CalendarPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  // Dialog states
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Always fetch full month of data to avoid refetch lag on view switch
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);

  const { eventsByDay, loading, createEvent, updateEvent, deleteEvent } = useEvents({
    startDate: format(monthStart, 'yyyy-MM-dd'),
    endDate: format(monthEnd, 'yyyy-MM-dd'),
  });

  // Handlers
  const handleOpenNewEvent = () => {
    setSelectedEvent(null);
    setEventFormOpen(true);
  };

  const handleOpenEditEvent = (event: Event) => {
    // Get the base event if this is a recurring instance
    const baseEvent = event.recurEvent || event;
    setSelectedEvent(baseEvent);
    setEventFormOpen(true);
  };

  const handleCloseEventForm = () => {
    setEventFormOpen(false);
    setSelectedEvent(null);
  };

  const handleSaveEvent = async (eventData: Partial<Event>, participants?: ParticipantInput[]) => {
    try {
      if (selectedEvent?.id) {
        await updateEvent(selectedEvent.id, eventData);
        enqueueSnackbar('Event updated', { variant: 'success' });
      } else {
        await createEvent(eventData, participants);
        enqueueSnackbar('Event created', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to save event', { variant: 'error' });
      throw err;
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent(eventId);
      enqueueSnackbar('Event deleted', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete event', { variant: 'error' });
      throw err;
    }
  };

  const days = useMemo(() => {
    return eachDayOfInterval({ start: monthStart, end: monthEnd });
  }, [monthStart, monthEnd]);

  const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const selectedEvents = selectedDateStr ? eventsByDay[selectedDateStr] || [] : [];

  // Search filtering - collect all events for the month and filter by query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const query = searchQuery.toLowerCase();
    const results: { date: string; event: Event }[] = [];
    Object.entries(eventsByDay).forEach(([dateStr, events]) => {
      events.forEach((event) => {
        const titleMatch = event.title?.toLowerCase().includes(query);
        const descMatch = event.description?.toLowerCase().includes(query);
        if (titleMatch || descMatch) {
          results.push({ date: dateStr, event });
        }
      });
    });
    // Sort by date
    results.sort((a, b) => a.date.localeCompare(b.date));
    return results;
  }, [searchQuery, eventsByDay]);

  const handlePrev = () => {
    if (viewMode === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };
  const handleNext = () => {
    if (viewMode === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };
  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Week view days
  const weekStart = startOfWeek(currentDate);
  const weekEnd = endOfWeek(currentDate);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);

  // Header title based on view mode
  const headerTitle = useMemo(() => {
    if (viewMode === 'month') return format(currentDate, 'MMMM yyyy');
    if (viewMode === 'week') {
      const ws = startOfWeek(currentDate);
      const we = endOfWeek(currentDate);
      return `${format(ws, 'MMM d')} - ${format(we, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  }, [viewMode, currentDate]);

  const getCalendarColor = (calendar: string): string => {
    const colorMap: Record<string, string> = {
      General: colors.calendar,
      Activities: colors.activities,
      Education: colors.school,
      Health: colors.health,
      Money: colors.money,
      Shopping: colors.calendar,
      Discipline: colors.discipline,
      Childcare: colors.calendar,
      Custody: colors.messages,
    };
    return colorMap[calendar] || colors.calendar;
  };

  if (loading) {
    return <LoadingSpinner message="Loading calendar..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="Manage your family schedule"
        actionLabel="New Event"
        actionIcon={AddIcon}
        onAction={handleOpenNewEvent}
      />

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search events by title or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {/* Search Results */}
      {searchResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Search Results ({searchResults.length} {searchResults.length === 1 ? 'event' : 'events'})
            </Typography>
            {searchResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No events matching "{searchQuery}"
              </Typography>
            ) : (
              <List disablePadding>
                {searchResults.map(({ date, event }) => (
                  <ListItemButton
                    key={`${date}-${event.id}`}
                    onClick={() => {
                      setSelectedDate(new Date(date + 'T00:00:00'));
                      setCurrentDate(new Date(date + 'T00:00:00'));
                      handleOpenEditEvent(event);
                    }}
                    sx={{
                      borderLeft: `3px solid ${getCalendarColor(event.calendar)}`,
                      pl: 2,
                      mb: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    <ListItemText
                      primary={event.title}
                      secondary={
                        `${format(new Date(date + 'T00:00:00'), 'EEE, MMM d')}${event.isAllDay ? ' - All day' : ` - ${format(new Date(event.startTime), 'h:mm a')}`}`
                      }
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                    <Chip
                      label={event.calendar}
                      size="small"
                      sx={{
                        bgcolor: `${getCalendarColor(event.calendar)}20`,
                        color: getCalendarColor(event.calendar),
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handlePrev}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="h6" fontWeight={600} sx={{ minWidth: 220 }}>
                {headerTitle}
              </Typography>
              <IconButton onClick={handleNext}>
                <ChevronRight />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ButtonGroup size="small" variant="outlined">
                {(['month', 'week', 'day'] as const).map((mode) => (
                  <Button
                    key={mode}
                    variant={viewMode === mode ? 'contained' : 'outlined'}
                    onClick={() => setViewMode(mode)}
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {mode}
                  </Button>
                ))}
              </ButtonGroup>
              <Button variant="outlined" size="small" onClick={handleToday}>
                Today
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Month View */}
      {viewMode === 'month' && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                {/* Day Headers */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                    <Box key={day} sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {day}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Calendar Days */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                  {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                    <Box key={`empty-${i}`} sx={{ minHeight: 90 }} />
                  ))}

                  {days.map((day) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const dayEvents = eventsByDay[dateStr] || [];
                    const isSelected = selectedDate && isSameDay(day, selectedDate);
                    const isTodayDate = isToday(day);

                    return (
                      <Box key={dateStr}>
                        <Box
                          onClick={() => setSelectedDate(day)}
                          sx={{
                            minHeight: 90,
                            p: 1,
                            cursor: 'pointer',
                            borderRadius: 1,
                            border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                            bgcolor: isTodayDate ? `${theme.palette.primary.main}10` : 'transparent',
                            overflow: 'hidden',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={isTodayDate ? 700 : 400}
                            color={isTodayDate ? 'primary.main' : 'text.primary'}
                          >
                            {format(day, 'd')}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {dayEvents.slice(0, 3).map((event) => (
                              <Box
                                key={event.id}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: getCalendarColor(event.calendar),
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                                  {event.isAllDay ? event.title : `${format(new Date(event.startTime), 'h:mm')} ${event.title}`}
                                </Typography>
                              </Box>
                            ))}
                            {dayEvents.length > 3 && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                +{dayEvents.length - 3} more
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}

                  {Array.from({ length: (7 - ((monthStart.getDay() + days.length) % 7)) % 7 }).map((_, i) => (
                    <Box key={`trailing-${i}`} sx={{ minHeight: 90 }} />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Event List Sidebar */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ position: 'sticky', top: 80 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {selectedDate ? format(selectedDate, 'EEEE, MMMM d') : 'Select a date'}
                </Typography>
                {selectedEvents.length === 0 ? (
                  <EmptyState
                    icon={EventIcon}
                    title="No events"
                    description="No events scheduled for this day"
                    actionLabel="Add Event"
                    onAction={handleOpenNewEvent}
                  />
                ) : (
                  <Box sx={{ maxHeight: 'calc(100vh - 240px)', overflowY: 'auto' }}>
                    <List disablePadding>
                      {selectedEvents.map((event) => (
                        <ListItemButton
                          key={event.id}
                          onClick={() => handleOpenEditEvent(event)}
                          sx={{
                            px: 0,
                            borderLeft: `3px solid ${getCalendarColor(event.calendar)}`,
                            pl: 2,
                            mb: 1,
                            borderRadius: 1,
                          }}
                        >
                          <ListItemText
                            primary={event.title}
                            secondary={
                              event.isAllDay
                                ? 'All day'
                                : `${format(new Date(event.startTime), 'h:mm a')} - ${format(new Date(event.endTime), 'h:mm a')}`
                            }
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          <Chip
                            label={event.calendar}
                            size="small"
                            sx={{
                              bgcolor: `${getCalendarColor(event.calendar)}20`,
                              color: getCalendarColor(event.calendar),
                              fontWeight: 500,
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Week View */}
      {viewMode === 'week' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
              {weekDays.map((day) => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayEvents = eventsByDay[dateStr] || [];
                const isTodayDate = isToday(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);

                return (
                  <Box key={dateStr}>
                    {/* Day header */}
                    <Box
                      onClick={() => setSelectedDate(day)}
                      sx={{
                        textAlign: 'center',
                        py: 1,
                        mb: 1,
                        cursor: 'pointer',
                        borderRadius: 1,
                        bgcolor: isTodayDate ? `${theme.palette.primary.main}10` : isSelected ? 'action.selected' : 'transparent',
                        borderBottom: `2px solid ${isTodayDate ? theme.palette.primary.main : 'transparent'}`,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {format(day, 'EEE')}
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={isTodayDate ? 700 : 400}
                        color={isTodayDate ? 'primary.main' : 'text.primary'}
                      >
                        {format(day, 'd')}
                      </Typography>
                    </Box>

                    {/* Events list */}
                    <Box sx={{ minHeight: 300 }}>
                      {dayEvents.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
                          No events
                        </Typography>
                      ) : (
                        dayEvents.map((event) => (
                          <Box
                            key={event.id}
                            onClick={() => handleOpenEditEvent(event)}
                            sx={{
                              p: 1,
                              mb: 0.5,
                              borderRadius: 1,
                              borderLeft: `3px solid ${getCalendarColor(event.calendar)}`,
                              bgcolor: `${getCalendarColor(event.calendar)}10`,
                              cursor: 'pointer',
                              '&:hover': { bgcolor: `${getCalendarColor(event.calendar)}20` },
                            }}
                          >
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {event.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {event.isAllDay ? 'All day' : format(new Date(event.startTime), 'h:mm a')}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Day View */}
      {viewMode === 'day' && (() => {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const dayEvents = eventsByDay[dateStr] || [];

        return (
          <Card>
            <CardContent>
              {dayEvents.length === 0 ? (
                <EmptyState
                  icon={EventIcon}
                  title="No events"
                  description={`No events scheduled for ${format(currentDate, 'MMMM d')}`}
                  actionLabel="Add Event"
                  onAction={handleOpenNewEvent}
                />
              ) : (
                <List disablePadding>
                  {dayEvents.map((event) => (
                    <ListItemButton
                      key={event.id}
                      onClick={() => handleOpenEditEvent(event)}
                      sx={{
                        borderLeft: `4px solid ${getCalendarColor(event.calendar)}`,
                        pl: 2,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: `${getCalendarColor(event.calendar)}08`,
                      }}
                    >
                      <ListItemText
                        primary={event.title}
                        secondary={
                          <>
                            {event.isAllDay
                              ? 'All day'
                              : `${format(new Date(event.startTime), 'h:mm a')} - ${format(new Date(event.endTime), 'h:mm a')}`}
                            {event.description && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {event.description}
                              </Typography>
                            )}
                            {typeof event.location === 'string' && event.location && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {event.location}
                              </Typography>
                            )}
                            {typeof event.location === 'object' && event.location?.address && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {event.location.address}
                              </Typography>
                            )}
                          </>
                        }
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '1rem' }}
                      />
                      <Chip
                        label={event.calendar}
                        size="small"
                        sx={{
                          bgcolor: `${getCalendarColor(event.calendar)}20`,
                          color: getCalendarColor(event.calendar),
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Event Form Dialog */}
      <EventFormDialog
        open={eventFormOpen}
        onClose={handleCloseEventForm}
        onSave={handleSaveEvent}
        onDelete={selectedEvent ? handleDeleteEvent : undefined}
        event={selectedEvent}
        initialDate={selectedDate || undefined}
      />
    </Box>
  );
};

export default CalendarPage;
